import React, { useEffect, useState } from 'react';
import AdminBar from './adminBar.jsx';
import supabase from '../../supabase.js';
import { Helmet } from 'react-helmet-async';

export default function Businessclaim() {
  const [pendingBusinesses, setPendingBusinesses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 20;

  // 🔹 helper: category → table name
  const getCategoryTableName = (category) => {
    if (!category) return null;
    // e.g. "Bank" -> "bank", "Jewelry Store" -> "jewelrystore"
    return category.toLowerCase().replace(/\s+/g, '');
  };

  // 🔹 initial fetch
  useEffect(() => {
    const fetchPending = async () => {
      setLoading(true);
      setErrorMsg('');

      const { data, error } = await supabase
        .from('businesses')
        .select('*')
        .eq('status', 'pending');

      if (error) {
        console.error('Fetch error:', error);
        setErrorMsg('Failed to load pending businesses.');
        setLoading(false);
        return;
      }

      // filter: only rows with non-empty www
      const filtered = (data || []).filter(
        (biz) => biz.www && biz.www.trim() !== ''
      );

      setPendingBusinesses(filtered);
      setLoading(false);
    };

    fetchPending();
  }, []);

  // pagination helpers
  const totalPages = Math.ceil(pendingBusinesses.length / PAGE_SIZE) || 1;
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const currentItems = pendingBusinesses.slice(
    startIndex,
    startIndex + PAGE_SIZE
  );

  const goPrev = () => {
    setCurrentPage((p) => Math.max(1, p - 1));
  };

  const goNext = () => {
    setCurrentPage((p) => Math.min(totalPages, p + 1));
  };

  // 🔹 approve single business
  const handleApprove = async (biz) => {
    try {
      setLoading(true);
      setErrorMsg('');

      // 1) update status to "claimed"
      const { error: updateError } = await supabase
        .from('businesses')
        .update({ status: 'claimed' })
        .eq('id', biz.id);

      if (updateError) {
        console.error('Update error:', updateError);
        setErrorMsg('Failed to approve this business.');
        setLoading(false);
        return;
      }

      // 2) delete from category table by www
      const tableName = getCategoryTableName(biz.category);
      if (tableName && biz.www) {
        const { error: deleteError } = await supabase
          .from(tableName)
          .delete()
          .eq('www', biz.www);

        if (deleteError) {
          console.error(
            `Delete error from ${tableName} for www=${biz.www}:`,
            deleteError
          );
          // we don't block approval if delete fails, just log it
        }
      }

      // 3) remove from local state
      setPendingBusinesses((prev) => prev.filter((b) => b.id !== biz.id));
    } finally {
      setLoading(false);
    }
  };

  // 🔹 decline single business
  const handleDecline = async (biz) => {
    try {
      setLoading(true);
      setErrorMsg('');

      const { error: updateError } = await supabase
        .from('businesses')
        .update({ status: 'rejected' })
        .eq('id', biz.id);

      if (updateError) {
        console.error('Update error:', updateError);
        setErrorMsg('Failed to decline this business.');
        setLoading(false);
        return;
      }

      setPendingBusinesses((prev) => prev.filter((b) => b.id !== biz.id));
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Approve all items on current page
  const handleApproveAll = async () => {
    if (currentItems.length === 0) return;

    setBulkLoading(true);
    setErrorMsg('');

    try {
      // we process each item sequentially to keep logic simple
      for (const biz of currentItems) {
        // 1) update businesses.status
        const { error: updateError } = await supabase
          .from('businesses')
          .update({ status: 'claimed' })
          .eq('id', biz.id);

        if (updateError) {
          console.error('Bulk update error:', updateError);
          // if something fails, just continue with the rest
          continue;
        }

        // 2) delete from category table
        const tableName = getCategoryTableName(biz.category);
        if (tableName && biz.www) {
          const { error: deleteError } = await supabase
            .from(tableName)
            .delete()
            .eq('www', biz.www);

          if (deleteError) {
            console.error(
              `Bulk delete error from ${tableName} for www=${biz.www}:`,
              deleteError
            );
          }
        }
      }

      // 3) remove these items from local state
      const idsToRemove = new Set(currentItems.map((b) => b.id));
      setPendingBusinesses((prev) => prev.filter((b) => !idsToRemove.has(b.id)));
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <div className="w-full flex flex-col  items-center bg-[#f9f9f9] mt-[50px] pb-[30px] min-h-screen">
             <Helmet>
              <title>Sitemanifest</title> 
              
              <meta name="robots" content="noindex, nofollow" />
              
              <meta name="googlebot" content="noarchive" />
            </Helmet>
      <AdminBar />

      <div className="w-full max-w-[900px] pt-[150px] px-[20px]">
        <div className="flex items-center justify-between mb-[20px]">
          <h1 className="text-[26px] font-semibold">
            Pending Business Claims
          </h1>

          <button
            onClick={handleApproveAll}
            disabled={bulkLoading || currentItems.length === 0}
            className="px-4 py-2 rounded-md bg-[#1E7D6B] text-white text-sm font-medium disabled:opacity-50"
          >
            {bulkLoading ? 'Approving...' : 'Approve all on this page'}
          </button>
        </div>

        {errorMsg && (
          <p className="text-red-500 text-sm mb-3">{errorMsg}</p>
        )}

        {loading && !bulkLoading && pendingBusinesses.length === 0 && (
          <p className="text-[16px] text-gray-600">Loading...</p>
        )}

        {!loading && pendingBusinesses.length === 0 ? (
          <p className="text-[16px] text-gray-600">No pending businesses.</p>
        ) : (
          <>
            <div className="flex flex-col gap-[15px] mb-[20px]">
              {currentItems.map((biz) => (
                <div
                  key={biz.id}
                  className="w-full p-[15px] bg-white rounded-lg shadow-sm border flex flex-col gap-2"
                >
                  <div className="flex justify-between items-start gap-3">
                    <div>
                      <h2 className="text-[20px] font-bold">{biz.title}</h2>
                      <p className="text-[15px] text-gray-600">{biz.www}</p>
                      <p className="text-[15px] text-gray-600">{biz.email}</p>
                      <p className="text-[14px] text-gray-500 mt-[5px]">
                        Category: {biz.category}
                      </p>
                    </div>

                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => handleApprove(biz)}
                        className="px-3 py-1 rounded-md bg-[#1E7D6B] text-white text-sm"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleDecline(biz)}
                        className="px-3 py-1 rounded-md bg-red-500 text-white text-sm"
                      >
                        Decline
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination controls */}
            {pendingBusinesses.length > PAGE_SIZE && (
              <div className="flex items-center justify-center gap-4">
                <button
                  onClick={goPrev}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded-md border text-sm disabled:opacity-50"
                >
                  Prev
                </button>
                <span className="text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={goNext}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded-md border text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
